import React, {useMemo} from 'react';
import {Box} from '@chakra-ui/react';
import {ColumnDef, createColumnHelper} from '@tanstack/react-table';
import {useCustomer} from '../../../hooks/api/metadata';
import {assertNever} from '../../../shared/util';
import {getFieldLabel} from '../../../shared/fields/types';
import {IntentFieldDefinition} from '../../../shared/fields/intent';
import {IntentSourceLabels} from '../../../shared/intent';
import {FieldsTable} from './FieldsTable';
import {CustomFieldMenu} from './CustomFieldMenu';

const intentColumnHelper = createColumnHelper<IntentFieldDefinition>();

const useIntentTableColumns = () => {
  const customer = useCustomer();
  const intentSources = useMemo(
    () => customer.intentSources ?? [],
    [customer.intentSources]
  );

  return useMemo(
    () =>
      [
        intentColumnHelper.accessor((field) => field, {
          id: 'label',
          header: 'Field Name',
          cell: (info) => {
            const field = info.getValue();
            return <Box whiteSpace="normal">{getFieldLabel(field)}</Box>;
          },
          size: 0,
        }),
        intentColumnHelper.accessor((field) => field, {
          id: 'source',
          header: 'Source',
          cell: (info) => {
            const field = info.getValue();

            let source;
            switch (field.source) {
              case 'linkedIn':
                source = 'LinkedIn';
                break;

              case 'aggregated': {
                const intentSource = intentSources.find(({id}) =>
                  id.equals(field.sourceId)
                );
                source = intentSource
                  ? IntentSourceLabels[intentSource.type]
                  : 'Unknown';
                break;
              }

              default:
                assertNever(field);
            }

            return <Box textColor="kgray.300">{source}</Box>;
          },
        }),
        intentColumnHelper.accessor((field) => field, {
          header: 'Last Updated',
          size: 200,
          cell: (info) => {
            const field = info.getValue();
            const lastUpdated = field.lastUpdated;
            return (
              <Box textColor="kgray.300">
                {lastUpdated ? lastUpdated.toLocaleDateString() : ''}
              </Box>
            );
          },
        }),
        intentColumnHelper.accessor('timestamp', {
          header: 'Created',
          size: 100,
          cell: (info) => {
            return (
              <Box textColor="kgray.300">
                {info.getValue().toLocaleDateString()}
              </Box>
            );
          },
        }),
        intentColumnHelper.accessor((field) => field, {
          id: 'actions',
          header: '',
          size: 50,
          cell: (info) => {
            return <CustomFieldMenu fieldDefinition={info.getValue()} />;
          },
        }),
      ] as ColumnDef<IntentFieldDefinition>[],
    [intentSources]
  );
};

// Intent fields table with different columns (no status, no accounts enriched, but has last updated)
export const IntentFieldsTable = ({
  fieldDefinitions,
}: {
  fieldDefinitions: IntentFieldDefinition[];
}) => {
  const columns = useIntentTableColumns();

  return <FieldsTable fieldDefinitions={fieldDefinitions} columns={columns} />;
};
